import { getAbilityData } from '../content/AbilityDataAdapter';
import type { AbilityStatusDef } from '../content/generated/abilities.gen';
import { MAX_PERIODIC_TICKS, MAX_STATUS_DURATION_MS } from './abilityStatusRules';

const EFFECT_IDS: Readonly<Record<string, string>> = {
  corruption: 'corruption', immolate: 'immolate', curse_of_agony: 'agony',
};
export const periodicEffectId = (abilityId: string): string | undefined =>
  Object.prototype.hasOwnProperty.call(EFFECT_IDS, abilityId) ? EFFECT_IDS[abilityId] : undefined;
export function periodicAbilityIdForEffect(effectId: string): string | undefined {
  return Object.keys(EFFECT_IDS).find(id => EFFECT_IDS[id] === effectId);
}

export interface PeriodicDamageSchedule {
  intervalMs: number;
  amounts: readonly number[];
  durationMs: number;
}

/** Fail closed even for an invalid runtime fixture; content has the same bounds. */
export function periodicStatusSchedule(statuses: readonly AbilityStatusDef[]): PeriodicDamageSchedule | null {
  const rows = statuses.filter(row => row.kind === 'periodic_damage');
  if (rows.length !== 1) return null;
  const { tickIntervalMs: interval, tickDamage: amounts, durationMs } = rows[0];
  if (!Number.isSafeInteger(interval) || interval! < 250 || interval! > 10000
    || !Array.isArray(amounts) || amounts.length < 1 || amounts.length > MAX_PERIODIC_TICKS
    || amounts.some(amount => !Number.isSafeInteger(amount) || amount <= 0)
    || durationMs !== interval! * amounts.length || durationMs > MAX_STATUS_DURATION_MS) return null;
  return { intervalMs: interval!, amounts: Object.freeze([...amounts]), durationMs };
}

export function abilityPeriodicSchedule(abilityId: string): PeriodicDamageSchedule | null {
  return periodicEffectId(abilityId) ? periodicStatusSchedule(getAbilityData(abilityId)?.statuses ?? []) : null;
}

/** Neighboring abilities still own legacy corruption, with their existing
 * authored values. A trusted admitted origin selects these, never wire data. */
export const LEGACY_CORRUPTION_ORIGINS = ['seed_of_corruption', 'siphon_life'] as const;
export function legacyCorruptionSpec(origin: string) {
  const tickDamage = origin === 'seed_of_corruption' ? 7 : origin === 'siphon_life' ? 5 : undefined;
  return tickDamage === undefined ? null : {
    effectId: 'corruption', durationMs: 10000, tickRateMs: 2000, tickDamage,
    data: { abilityId: origin },
  };
}
